import {ApplicationModalStep} from "../types/ApplicationModalStep";

export interface IApplicationModalStateContext {
    isMultileadEnabled: boolean;
    needsMarketingConsent: boolean;
    isAutoresponderEnabled: boolean;
    forceClose: boolean;
}

export type ApplicationModalStateEvent =
    | {type: "OPEN"; step?: ApplicationModalStep}
    | {type: "FORM_SUBMITTED"}
    | {type: "FORM_CLOSED"}
    | {type: "MULTILEAD_COMPLETED"}
    | {type: "MULTILEAD_NO_RECOMMENDATIONS"}
    | {type: "MULTILEAD_CLOSED"}
    | {type: "CONSENT_COMPLETED"}
    | {type: "SUCCESS_INFO_NEXT"};

type TransitionFn = (ctx: IApplicationModalStateContext, event: ApplicationModalStateEvent) => ApplicationModalStep;
type StepTransitions = Partial<Record<ApplicationModalStateEvent["type"], TransitionFn>>;

const handleFormSubmitted: TransitionFn = (ctx) => {
    if (ctx.isMultileadEnabled) return ApplicationModalStep.MULTILEAD;
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

const handleFormClosed: TransitionFn = () => ApplicationModalStep.CLOSE;

const handleFormClosedWithGoodbye: TransitionFn = (ctx) => {
    if (ctx.isAutoresponderEnabled || ctx.forceClose) return ApplicationModalStep.CLOSE;
    return ApplicationModalStep.GOODBYE_INFO;
};

const handleMultileadNext: TransitionFn = (ctx) => {
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

const transitions: Partial<Record<ApplicationModalStep, StepTransitions>> = {
    [ApplicationModalStep.WAITING]: {
        OPEN: (_ctx, event) => ("step" in event && event.step) || ApplicationModalStep.APPLICATION_FORM_STANDARD
    },

    [ApplicationModalStep.APPLICATION_FORM_STANDARD]: {
        FORM_SUBMITTED: handleFormSubmitted,
        FORM_CLOSED: handleFormClosedWithGoodbye
    },

    [ApplicationModalStep.APPLICATION_FORM_FULL_OFFER]: {
        FORM_SUBMITTED: handleFormSubmitted,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC]: {
        FORM_SUBMITTED: handleFormSubmitted,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.MULTILEAD]: {
        MULTILEAD_COMPLETED: handleMultileadNext,
        MULTILEAD_NO_RECOMMENDATIONS: handleMultileadNext,
        MULTILEAD_CLOSED: () => ApplicationModalStep.CLOSE
    },

    [ApplicationModalStep.NOTIFICATION_CONSENT]: {
        CONSENT_COMPLETED: () => ApplicationModalStep.CLOSE,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.GOODBYE_INFO]: {
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.SUCCESS_INFO]: {
        SUCCESS_INFO_NEXT: () => ApplicationModalStep.CLOSE,
        FORM_CLOSED: handleFormClosed
    }
};

/**
 * Resolves the next step given the current step, an event, and the application modal state context.
 * Returns null if no valid transition exists for the current step + event combination.
 */
export function resolveNextStep(
    currentStep: ApplicationModalStep,
    event: ApplicationModalStateEvent,
    context: IApplicationModalStateContext
): ApplicationModalStep | null {
    const stepTransitions = transitions[currentStep];
    if (!stepTransitions) return null;

    const transitionFn = stepTransitions[event.type];
    if (!transitionFn) return null;

    return transitionFn(context, event);
}
